import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import PromptCard from '@/components/PromptCard';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Globe, ExternalLink, HelpCircle, UserPlus, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlatformDetail() {
  const { slug } = useParams();
  const [platform, setPlatform] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/platform/${slug}`).then(r => setPlatform(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/platform/${slug}/prompts?page=${page}&limit=18`)
      .then(r => {
        setPrompts(r.data.prompts);
        setTotalPages(r.data.pages);
        setTotal(r.data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug, page]);

  if (!platform && !loading) return <div className="flex justify-center py-20 text-muted-foreground">Platform not found</div>;
  if (!platform) return <div className="flex justify-center py-20 text-muted-foreground">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="platform-detail-page">
      {/* Back */}
      <Link to="/platforms" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="back-link">
        <ChevronLeft className="w-4 h-4" /> All Platforms
      </Link>

      {/* Platform Header */}
      <Card className="p-6 sm:p-8 border-border/50 mb-8" data-testid="platform-header">
        <div className="flex items-start gap-5">
          {platform.favicon_url ? (
            <img
              src={platform.favicon_url}
              alt={platform.name}
              className="w-14 h-14 rounded-xl object-contain bg-muted p-1.5 shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
              data-testid="platform-favicon"
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center text-xl font-bold shrink-0">
              {platform.name?.charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold" data-testid="platform-name">{platform.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {platform.crawled_description || platform.description || 'AI platform'}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              {platform.website && (
                <a href={platform.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid="platform-website-link">
                  <Globe className="w-3.5 h-3.5" /> {new URL(platform.website).hostname}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {platform.signup_url && (
                <a href={platform.signup_url} target="_blank" rel="noopener noreferrer" data-testid="platform-signup-link">
                  <Button size="sm" className="rounded-full gap-1.5 h-8 text-xs">
                    <UserPlus className="w-3.5 h-3.5" /> Sign Up
                  </Button>
                </a>
              )}
              {platform.help_url && (
                <a href={platform.help_url} target="_blank" rel="noopener noreferrer" data-testid="platform-help-link">
                  <Button size="sm" variant="outline" className="rounded-full gap-1.5 h-8 text-xs">
                    <HelpCircle className="w-3.5 h-3.5" /> Help & Docs
                  </Button>
                </a>
              )}
              <Badge variant="outline" className="rounded-full text-xs">{total} prompts</Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* Prompts Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading prompts...</div>
      ) : prompts.length === 0 ? (
        <Card className="p-12 text-center border-border/50">
          <p className="text-muted-foreground">No prompts for this platform yet.</p>
          <Link to="/submit">
            <Button className="mt-4 rounded-full">Share a Prompt</Button>
          </Link>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="prompts-grid">
            {prompts.map(p => <PromptCard key={p.id} prompt={p} />)}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8" data-testid="pagination">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full gap-1"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full gap-1"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                data-testid="next-page-btn"
              >
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
